/**
 * Order Payload Builder Utilities
 * Alternative reusable functions for building order payloads
 * This file provides an alternative approach to orderUtils.ts
 */

import { parsePrice } from './priceUtils';
import type { ModifierGroup, ModifierOption } from '../redux/slices/modifiersSlice';

// ============================================================================
// TYPES
// ============================================================================

export interface EnrichedModifierOption {
  id: string;
  restaurantId: string;
  name: string;
  price: string;
  image: string;
  isEnabled: boolean;
  childGroupId: string;
  posReferenceId: string;
}

export interface EnrichedModifierGroup {
  id: string;
  restaurantId: string;
  posReferenceId: string;
  name: string;
  isRequired: boolean;
  allowMultiples: boolean;
  minRequired: number;
  maxAllowed: number;
  sortOrder: number;
  options: EnrichedModifierOption[];
}

export interface LineItem {
  id: string;
  name: string;
  posReferenceId: string;
  quantity: number;
  price: number;
  image: string;
  modifier: EnrichedModifierGroup[];
}

export interface OrderAddress {
  address: string;
  city: string;
  state: string;
  country: string;
  zipcode: number;
}

export interface OrderCustomer {
  name: string;
  phone: string;
  email: string;
  address: OrderAddress;
}

export interface OrderPayload {
  customer: OrderCustomer;
  source: 'web';
  fulfillmentType: 'pickup' | 'delivery';
  notes: string;
  paymentMode: 'pay_now' | 'pay_later';
  lineItems: LineItem[];
  callId: string;
  pickupDetails?: {
    scheduledFor: string; // ISO 8601 format
  };
  quoteId?: string; // Uber Direct delivery quote ID
  deliveryFee?: number; // Delivery fee from quote
  tip?: number; // Tip amount for delivery orders
}

// ============================================================================
// MODIFIER ENRICHMENT FUNCTIONS
// ============================================================================

/**
 * Enrich a single modifier option with full API data
 * 
 * @param selectedOption - Option selected by user from cart
 * @param availableOptions - All modifier options from API
 * @param restaurantId - Current restaurant ID
 * @returns Enriched modifier option with all required fields
 */
export const enrichModifierOption = (
  selectedOption: any,
  availableOptions: ModifierOption[],
  restaurantId: string
): EnrichedModifierOption => {
  // Find matching option from API data
  const matchedOption = availableOptions.find(
    (apiOption) => apiOption.id === selectedOption.id
  );

  // Use matched data or fallback to selected option
  const optionData = matchedOption || selectedOption;

  return {
    id: optionData.id,
    restaurantId,
    name: optionData.name,
    price: String(selectedOption.price || optionData.price || 0),
    image: optionData.image || '',
    isEnabled: optionData.isEnabled !== false,
    childGroupId: optionData.childGroupId || '',
    posReferenceId: optionData.posReferenceId || '',
  };
};

/**
 * Enrich a modifier group with its options
 * 
 * @param modifierGroup - Modifier group selected by user from cart
 * @param availableGroups - All modifier groups from API
 * @param availableOptions - All modifier options from API
 * @param restaurantId - Current restaurant ID
 * @returns Enriched modifier group with enriched options
 */
export const enrichModifierGroup = (
  modifierGroup: any,
  availableGroups: ModifierGroup[],
  availableOptions: ModifierOption[],
  restaurantId: string
): EnrichedModifierGroup => {
  // Find matching group from API data
  const matchedGroup = availableGroups.find(
    (apiGroup) => apiGroup.id === modifierGroup.id
  );

  // Use matched data or fallback to cart data
  const groupData = matchedGroup || { ...modifierGroup, restaurantId };

  // Enrich all options in this group
  const enrichedOptions = (modifierGroup.options || []).map((selectedOption: any) =>
    enrichModifierOption(selectedOption, availableOptions, restaurantId)
  );

  return {
    id: groupData.id,
    restaurantId,
    posReferenceId: groupData.posReferenceId || '',
    name: groupData.name,
    isRequired: groupData.isRequired || false,
    allowMultiples: groupData.allowMultiples || false,
    minRequired: groupData.minRequired || 0,
    maxAllowed: groupData.maxAllowed || 0,
    sortOrder: groupData.sortOrder || 0,
    options: enrichedOptions,
  };
};

/**
 * Enrich all modifiers for a line item
 * 
 * @param selectedModifiers - Modifiers selected by user from cart
 * @param availableGroups - All modifier groups from API
 * @param availableOptions - All modifier options from API
 * @param restaurantId - Current restaurant ID
 * @returns Array of enriched modifier groups
 */
export const enrichLineItemModifiers = (
  selectedModifiers: any[],
  availableGroups: ModifierGroup[],
  availableOptions: ModifierOption[],
  restaurantId: string
): EnrichedModifierGroup[] => {
  return selectedModifiers.map((modifierGroup) =>
    enrichModifierGroup(modifierGroup, availableGroups, availableOptions, restaurantId)
  );
};

// ============================================================================
// LINE ITEM BUILDING FUNCTIONS
// ============================================================================

/**
 * Find menu item posReferenceId from various sources
 * 
 * @param itemId - Menu item ID
 * @param menuItem - Menu item from cart
 * @param menuItems - All menu items from API
 * @param menuItemDetails - Menu item details from API
 * @returns posReferenceId or empty string
 */
export const findMenuItemPosReferenceId = (
  itemId: string,
  menuItem: any,
  menuItems: any[],
  menuItemDetails: any
): string => {
  // Try menuItemDetails first (single item, not array)
  if (menuItemDetails?.item) {
    const detailsItem = menuItemDetails.item;
    if ((detailsItem.pk_id || detailsItem.id) === itemId) {
      return detailsItem.posReferenceId || '';
    }
  }

  // Fallback to menuItems list
  const matchedMenuItem = menuItems.find(
    (apiItem: any) => (apiItem.pk_id || apiItem.id) === itemId
  );
  if (matchedMenuItem?.posReferenceId) {
    return matchedMenuItem.posReferenceId;
  }

  // Last fallback: check cart item itself
  return menuItem.posReferenceId || '';
};

/**
 * Build a line item from cart item
 * 
 * @param cartItem - Cart item with menu item and modifiers
 * @param menuItems - All menu items from API
 * @param menuItemDetails - Menu item details from API
 * @param modifierGroups - All modifier groups from API
 * @param modifierOptions - All modifier options from API
 * @param restaurantId - Current restaurant ID
 * @returns Complete line item for order payload
 */
export const buildLineItem = (
  cartItem: any,
  menuItems: any[],
  menuItemDetails: any,
  modifierGroups: ModifierGroup[],
  modifierOptions: ModifierOption[],
  restaurantId: string
): LineItem => {
  const menuItem = cartItem.item;
  const selectedModifiers = cartItem.selected?.modifiers || [];
  const itemId = menuItem.pk_id || menuItem.id;

  // Find posReferenceId
  const posReferenceId = findMenuItemPosReferenceId(
    itemId,
    menuItem,
    menuItems,
    menuItemDetails
  );

  // Enrich modifiers
  const enrichedModifiers = enrichLineItemModifiers(
    selectedModifiers,
    modifierGroups,
    modifierOptions,
    restaurantId
  );

  return {
    id: itemId,
    name: menuItem.name,
    posReferenceId,
    quantity: cartItem.quantity,
    price: parsePrice(menuItem.price),
    image: menuItem.image || '',
    modifier: enrichedModifiers,
  };
};

// ============================================================================
// ORDER PAYLOAD BUILDING FUNCTION
// ============================================================================

/**
 * Build complete order payload from form data and cart
 * 
 * @param formData - Checkout form data
 * @param orderType - Order type (pickup/delivery)
 * @param paymentMode - Payment mode (pay_now/pay_later)
 * @param cartItems - Items in cart
 * @param selectedRestaurant - Selected restaurant
 * @param modifiersState - Modifier groups and options from Redux
 * @param menuItems - All menu items from API
 * @param menuItemDetails - Menu item details from API
 * @param quoteId - Uber Direct delivery quote ID (optional, for delivery orders)
 * @returns Complete order payload ready for API submission
 */
export const buildOrderPayload = (
  formData: any,
  orderType: 'pickup' | 'delivery',
  paymentMode: 'pay_now' | 'pay_later',
  cartItems: any[],
  selectedRestaurant: any,
  modifiersState: { groups: ModifierGroup[]; options: ModifierOption[] },
  menuItems: any[],
  menuItemDetails: any,
  quoteId?: string,
  deliveryFee?: number
): OrderPayload => {
  const restaurantId = selectedRestaurant?.restaurant_id || '';
  const availableModifierGroups = modifiersState.groups || [];
  const availableModifierOptions = modifiersState.options || [];
  const availableMenuItems = menuItems || [];

  // Build line items
  const lineItems = cartItems.map((cartItem) =>
    buildLineItem(
      cartItem,
      availableMenuItems,
      menuItemDetails,
      availableModifierGroups,
      availableModifierOptions,
      restaurantId
    )
  );

  // Build base order payload
  const payload: OrderPayload = {
    customer: {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      address: {
        address: formData.address_line_1 || '',
        city: formData.locality || '',
        state: formData.administrative_district_level_1 || '',
        country: formData.country || 'US',
        zipcode: parseInt(formData.postal_code || '0'),
      },
    },
    source: 'web',
    fulfillmentType: orderType,
    notes: formData.specialRequests || '',
    paymentMode,
    lineItems,
    callId: '',
  };

  // Add pickupDetails only if pickup is scheduled
  if (orderType === 'pickup' && formData.isScheduled && formData.scheduledDate && formData.scheduledTime) {
    const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}:00`);
    payload.pickupDetails = {
      scheduledFor: scheduledDateTime.toISOString(),
    };
  }

  // Add quoteId for delivery orders
  if (orderType === 'delivery' && quoteId) {
    payload.quoteId = quoteId;
  }

  // Add deliveryFee for delivery orders
  if (orderType === 'delivery' && deliveryFee !== undefined) {
    payload.deliveryFee = deliveryFee;
  }

  // Add tip for delivery orders
  if (orderType === 'delivery' && formData.tip && formData.tip > 0) {
    payload.tip = formData.tip;
  }

  return payload;
};
